// The Booking cross-roster pipeline BODY — the sections under BookingRosterScreen's header. The pipeline board
// leads: Active deals → [Routing, Top Cities | Recent settlements] → the email stub (PO, ART-140). The pair of
// TABLE sections sits in one row (PO, 2026-09-02) and collapses back to a stack below 1280px, because at half a
// narrow column a six-column table scrolls inside its own card.
//
// No "$X awaiting" sum (PO, ART-140). The old "Needs attention" section went with it and has since come back as
// its own read, ABOVE this body (ART-324, the product owner's later ruling) — `BookingRosterScreen` mounts it.
// `booking.attention` / `booking.receivables` still ride the DTO and are not rendered.
//
// MONEY-BEARING (recent settlements carry cents). Reads every field defensively (the generated types mark all
// optional/nullable); born-blank sections show a single muted line, never a fabricated number and never a
// screen-tall centred block.
//
// Two structural changes from the first build (2026-09-01):
//   • HOLDS ARE THE MIDDLE COLUMN, not a card. A hold is `artist.shows.status='hold'` — one value in the
//     five-value `ck_shows_status` pipeline — so it belongs in the kanban's Hold stage. The standalone
//     "Holds · live countdowns" card was rendering the same rows a second time beside two empty columns.
//   • DISCOVER IS GONE, screen and all (parity: legacy has no equivalent). The support-act projection it
//     searched is untouched and still reached from the artist side, per show, via the show editor's
//     support-finder — so opted-in bands stay discoverable; only the cross-roster genre+city search is gone.
//
// Every drill-in on this page lands on the artist's own Booking tab. The cross-roster view deliberately owns
// no per-row write: a hold is released or confirmed against a SHOW, so the surface that owns the record owns
// the action. Legacy reached the same conclusion and rendered its roster-level controls inert; ours navigate
// instead, which is the same rule with a destination.
import { useMemo } from "react";
import { Mail } from "lucide-react";
import { Card, DataTable, Icon, compactNumber, type DataTableColumn } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { DealsBoard, BOOKING_COLUMNS, type DealCard } from "./DealsBoard.js";
import { calendarTypeFor, soonerOf } from "./dealSort.js";
import type { RosterDealTarget } from "./RosterDealModal.js";
import { settlementStatusLabel } from "./dealVocab.js";
import { formatDay, holdCountdown, holdDaysLeft, offerDealLine, offerGoodThrough } from "./rosterFormat.js";
import styles from "./BookingRoster.module.scss";

type BookingBlock = components["schemas"]["RosterBookingBlock"];
type DealRow = components["schemas"]["RosterDealRow"];
type OfferRow = components["schemas"]["RosterOfferRow"];
type RoutingRow = components["schemas"]["RosterRoutingRow"];
type SettlementLine = components["schemas"]["RosterSettlementLine"];

/** The kind TAG on a deal/offer card — "Show" / "Festival", the spec's `bk-tag`. */
function kindTag(kind: string | null | undefined): string {
  return kind === "festival" ? "Festival" : "Show";
}

function usd(cents: number | null | undefined): string {
  if (cents == null) return "—"; // born-blank: a null net-to-artist renders '—', never a fabricated $0
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function cityLine(city: string | null | undefined, state: string | null | undefined): string {
  return [city, state].filter(Boolean).join(", ");
}

/**
 * A cross-roster deal as an ENRICHED kanban card (ART-139). The headline is the ARTIST (this is the roster
 * board — you need to know whose date it is), the sub-line is "city, state · venue", and a HOLD additionally
 * carries the Date · Amount · Buyer mini-row and the deal note. The countdown reads "Nd left · exp <date>" and
 * turns hot (accent) at ≤3 days; a hold with no expiry omits the whole right side rather than printing "No
 * expiry". A confirmed show keeps the guarantee on its foot (no expiry to count to).
 *
 * The card OPENS the show (part 3): a hold to the show editor (a date still negotiated), a confirmed one to
 * the read-only preview — the roster show routes, which resolve the band from the URL, so this works from the
 * band-less scope shell. A Ready-to-confirm card carries Confirm show / Cancel show (ART-330), which run under the
 * deal's own band through `RosterDealModal`.
 */
function dealCard(deal: DealRow, money: Map<string, number | null>, onOpenDeal?: (t: RosterDealTarget) => void): DealCard {
  const isHold = deal.status === "hold";
  // ART-326: a hold the team is in on sits in Ready to confirm.
  const ready = isHold && !!deal.ready;
  const invited = deal.invitedCount ?? 0;
  const exp = deal.holdExpiresOn;
  // The guarantee is lined up from the PARALLEL money list by show id. `has` distinguishes the two states the
  // shared row could not: no entry at all ⇒ this seat cannot see money (render nothing); an entry whose figure
  // is null ⇒ the deal has no guarantee set yet (render the em dash).
  const has = deal.showId != null && money.has(deal.showId);
  const gtee = has ? money.get(deal.showId!) : undefined;
  const amount = has ? (gtee != null ? usd(gtee) : null) : null;
  const subtitle = [cityLine(deal.city, deal.state), deal.venueName].filter(Boolean).join(" · ");
  // Opens the show OVER this page — the editor for a hold, the read-only preview for a confirmed one — under a
  // transient band context, so closing returns HERE rather than dropping onto that artist's own booking tab.
  const open =
    deal.bandId && deal.showId && onOpenDeal
      ? () => onOpenDeal({ kind: isHold ? "hold" : "confirmed", bandId: deal.bandId!, showId: deal.showId! })
      : undefined;

  return {
    key: deal.showId ?? `${deal.bandId}-${deal.date}`,
    sortKey: soonerOf(deal.date, deal.holdExpiresOn),
    status: ready ? "ready" : deal.status,
    tag: ready ? "Ready to confirm" : isHold ? (invited > 0 ? `Hold · ${deal.inCount ?? 0} of ${invited} in` : "Hold") : kindTag(deal.kind),
    tagTone: ready ? "ready" : isHold ? "hold" : undefined,
    title: deal.artistName || "TBD",
    subtitle: isHold ? subtitle || "—" : subtitle || null,
    // A hold's expiry drives the countdown; no expiry ⇒ no right side (never "No expiry"). Hot at ≤3 days.
    meta: isHold ? (exp ? `${holdCountdown(exp)} · exp ${formatDay(exp)}` : null) : formatDay(deal.date),
    metaTone: isHold && exp && (holdDaysLeft(exp) ?? 99) <= 3 ? "hot" : undefined,
    kv: isHold
      ? [
          { label: "Date", value: formatDay(deal.date) },
          // The Amount cell is OMITTED when this seat cannot see money (no entry in the list) — an absent cell
          // says "not your business", where a "—" would read as "no guarantee set". Present-with-null is the
          // second sentence, and it keeps the "—".
          ...(has ? [{ label: "Amount", value: amount }] : []),
          { label: "Buyer", value: deal.promoter?.trim() || null },
        ]
      : undefined,
    note: isHold ? deal.notes?.trim() || null : null,
    // A confirmed show keeps its guarantee on the foot; a hold shows it in the kv row above instead.
    trailing: !isHold && has ? (gtee != null ? usd(gtee) : "—") : null,
    onOpen: open,
    // ART-330 — the agent finishes the deal from here: confirm it out to everyone, or kill it.
    actions: ready && deal.bandId && deal.showId && onOpenDeal
      ? [
          {
            label: "Confirm show",
            onClick: () => onOpenDeal({ kind: "confirm", bandId: deal.bandId!, showId: deal.showId!, label: dealLabel(deal) }),
          },
          {
            label: "Cancel show",
            tone: "danger" as const,
            onClick: () => onOpenDeal({ kind: "cancel", bandId: deal.bandId!, showId: deal.showId!, label: dealLabel(deal) }),
          },
        ]
      : undefined,
  };
}

/** "Exit · Oct 31" — how a deal is named in its confirm prompt. */
function dealLabel(deal: DealRow): string {
  return [deal.venueName?.trim() || deal.artistName || "This show", formatDay(deal.date)].filter(Boolean).join(" · ");
}

/**
 * A pre-hold OFFER on the cross-roster board (ART-139). Rolled up from every artist's shortlist so the Offer
 * column is the roster's offers rather than born-empty. Clicking it opens the stylised details view — with its
 * Convert / Pass actions — OVER this page (ART-139), under a transient band context for the offer's own band.
 */
function offerCard(offer: OfferRow, onOpenDeal?: (t: RosterDealTarget) => void): DealCard {
  // ART-326: the Offer column's three states.
  const st = offer.status ?? "soft_hold";
  return {
    key: offer.offerId ?? `${offer.bandId}-${offer.date}`,
    sortKey: soonerOf(offer.date, offer.goodThrough),
    status: "offer",
    tag: st === "requested" ? "Waiting on artist" : st === "passed" ? "Passed by team" : "Soft hold",
    tagTone: st === "requested" ? "wait" : st === "passed" ? "pass" : "soft",
    dashed: st === "soft_hold",
    title: offer.artistName || "TBD",
    subtitle: offer.buyerVenue?.trim() || "—",
    meta: formatDay(offer.date),
    detail: offer.goodThrough ? offerGoodThrough(offer.goodThrough) : "No deadline",
    trailing: offerDealLine(offer.amountCents, offer.dealType, offer.doorSplitPct),
    onOpen:
      offer.bandId && offer.offerId && onOpenDeal
        ? () => onOpenDeal({ kind: "offer", bandId: offer.bandId!, offerId: offer.offerId! })
        : undefined,
  };
}

/** A settlement money cell. A door deal reports no figure — legacy prints "Split" rather than a number it
 *  could not compute, and so do we (the server sends none). A settled row's balance is a dash, not $0. */
function settlementCell(value: number | null | undefined, isDoor: boolean, blankWhenZero = false): string {
  if (isDoor) return "Split";
  if (value == null) return "—";
  if (blankWhenZero && value === 0) return "—";
  return usd(value);
}

/** The routing verdict's human label. The server sends the raw verdict; the words are ours. */
function actionLabel(action: string | null | undefined): string {
  switch (action) {
    case "book":
      return "Book";
    case "warm":
      return "Warm";
    case "route":
      return "Route";
    default:
      return action ?? "";
  }
}

/** The routing table's columns. `openBooking` is threaded so the artist cell can drill in. */
function routingColumns(openBooking: (bandId: string | null | undefined) => void): DataTableColumn<RoutingRow>[] {
  return [
    {
      key: "artist",
      header: "Artist",
      cell: (r) => (
        <button type="button" className={styles.rowLink} onClick={() => openBooking(r.bandId)} title={r.artistName ?? undefined}>
          {r.artistName}
        </button>
      ),
    },
    {
      key: "city",
      header: "City",
      cell: (r) => (
        <>
          {r.city}
          {r.region && <span className={styles.subtle}>{r.region}</span>}
        </>
      ),
    },
    { key: "total", header: "Total audience", align: "end", cell: (r) => compactNumber(r.totalAudience ?? 0) },
    { key: "spotify", header: "Spotify", align: "end", cell: (r) => compactNumber(r.spotifyAudience ?? 0) },
    // Never played there is a real answer, not a blank cell.
    { key: "last", header: "Last show", cell: (r) => (r.lastShowOn ? formatDay(r.lastShowOn) : "—") },
    {
      key: "action",
      header: "Action",
      cell: (r) => <span className={styles.pill} data-action={r.action}>{actionLabel(r.action)}</span>,
    },
  ];
}

/** The settlements table's columns — legacy's Artist · Date · Venue · Total · Balance · Status. */
function settlementColumns(openBooking: (bandId: string | null | undefined) => void): DataTableColumn<SettlementLine>[] {
  return [
    {
      key: "artist",
      header: "Artist",
      cell: (s) => (
        <button type="button" className={styles.rowLink} onClick={() => openBooking(s.bandId)} title={s.artistName ?? undefined}>
          {s.artistName}
        </button>
      ),
    },
    { key: "date", header: "Date", cell: (s) => formatDay(s.date) },
    { key: "venue", header: "Venue", cell: (s) => s.venueName ?? "—" },
    { key: "total", header: "Total", align: "end", cell: (s) => settlementCell(s.totalCents, s.isDoorDeal ?? false) },
    {
      key: "balance",
      header: "Balance",
      align: "end",
      // A settled row has nothing outstanding, so its balance is a dash — a column of $0s reads as money
      // still owed.
      cell: (s) => (s.status === "settled" ? "—" : settlementCell(s.balanceCents, s.isDoorDeal ?? false, true)),
    },
    {
      key: "status",
      header: "Status",
      cell: (s) => <span className={styles.pill} data-settle={s.status}>{settlementStatusLabel(s.status)}</span>,
    },
  ];
}

export function BookingRosterOverview({
  booking,
  onNavigate,
  onNewOffer,
  onOpenDeal,
}: {
  booking: BookingBlock;
  onNavigate?: (route: string) => void;
  /** Open the "+ New offer" band picker — owned by the SCREEN (it also drives the header action). */
  onNewOffer?: () => void;
  /** Open a card's record OVER this page (offer details / show editor / preview) — owned by the SCREEN. */
  onOpenDeal?: (target: RosterDealTarget) => void;
}) {
  const deals = booking.deals ?? [];
  const offers: OfferRow[] = booking.offers ?? [];
  const routing = booking.routing ?? [];
  const settlements: SettlementLine[] = booking.recentSettlements ?? [];

  // The parallel money list, indexed by show id. An ABSENT entry means this seat cannot see money; a present
  // entry with a null figure means the deal has no guarantee set. The board must be able to say both.
  const dealMoney = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const row of booking.dealMoney ?? []) if (row.showId) m.set(row.showId, row.guaranteeCents ?? null);
    return m;
  }, [booking.dealMoney]);
  // Offers first (the pipeline's leading stage), then holds/confirmed — DealsBoard buckets by status either
  // way, but building them in stage order keeps a stable read.
  // A confirmed show leaves the board for the Booking tab (ART-326): Offer → Hold → Ready to confirm.
  const dealCards: DealCard[] = [
    ...offers.map((o) => offerCard(o, onOpenDeal)),
    ...deals.filter((d) => d.status === "hold").map((d) => dealCard(d, dealMoney, onOpenDeal)),
  ];

  // Every drill-in lands on that artist's Booking tab — the surface that owns the action. The cross-roster
  // page deliberately has no per-row write of its own: a hold is released or confirmed against a show.
  const openBooking = (bandId: string | null | undefined) =>
    bandId && onNavigate?.(`/roster/artists/${bandId}/calendar`);

  return (
    <>
      {/* Active deals — the booking pipeline, offer → hold → confirmed, leads the page (PO, ART-140). Offers
          are rolled up from every artist's shortlist (ART-139), so the Offer column is the roster's offers
          rather than born-empty; holds and confirmed are `artist.shows`. "+ New offer" picks an artist first
          — an offer must be born ON one (PO, 2026-09-02) — then lands on that artist's dashboard with the
          create form open. */}
      <DealsBoard
        cards={dealCards}
        title="Active deals"
        subtitle="offer → hold → ready to confirm"
        columns={BOOKING_COLUMNS}
        pinColumns
        onNewDeal={onNewOffer}
        // ART-331 — each column opens the roster's booking calendar filtered to it; View all, unfiltered.
        onOpenColumn={(col) => onNavigate?.(`/roster/g/calendar?type=${calendarTypeFor(col)}`)}
        onViewAll={() => onNavigate?.("/roster/g/calendar")}
      />

      {/* Routing and Recent settlements sit SIDE BY SIDE — both are a bounded DataTable in a Card
          (12 rows apiece, capped server-side), so the pair fills one row without either half running
          away from the other. Below the breakpoint the grid is one column and they stack in this same
          order. */}
      <div className={styles.twoUp}>
        {/* Routing, Top Cities — markets to fill, from analytics.audience_cities joined to each artist's last
            show there. The Book / Warm / Route verdict is the server's (legacy's computeAction floors); the
            words are the client's. No "Open Top Cities →" header link: there is no cross-roster Top Cities page
            to send anyone to, and legacy's own version of that link was rendered permanently aria-disabled.
            The ARTIST cell drills in instead, which is a destination that exists. */}
        <Card
          title="Routing, Top Cities"
          subtitle="Markets to fill across the roster · pulled from each artist's Top Cities"
        >
          {routing.length === 0 ? (
            <p className={styles.emptyLine}>No markets to fill yet — routing appears once your artists' audience syncs.</p>
          ) : (
            <DataTable
              rows={routing}
              rowKey={(r, i) => `${r.bandId}-${r.city}-${i}`}
              columns={routingColumns(openBooking)}
            />
          )}
        </Card>

        {/* Recent settlements — the money-bearing read-through, in legacy's model: what is OWED against what
            has arrived. Every status is read, so the card can show what is still outstanding rather than only
            business already closed. */}
        <Card
          title="Recent settlements"
          subtitle="Manual today · VC auto-flow Fall 2026"
        >
          {settlements.length === 0 ? (
            <p className={styles.emptyLine}>No guarantee settlements to track.</p>
          ) : (
            <DataTable
              rows={settlements}
              rowKey={(s, i) => `${s.showId}-${i}`}
              columns={settlementColumns(openBooking)}
            />
          )}
        </Card>
      </div>

      {/* The email stub — chrome, always rendered, never a titled card with a centred empty pretending to be
          a section awaiting data. */}
      <section className={styles.soon} aria-label="Email">
        <span className={styles.soonIcon} aria-hidden>
          <Icon icon={Mail} size={20} />
        </span>
        <div className={styles.soonBody}>
          <span className={styles.soonTitle}>Email, coming Fall 2026</span>
          <p className={styles.soonCopy}>
            Offers, confirmations, and settlements will land in a shared booking inbox here.
          </p>
        </div>
        <span className={styles.vcBadge}>+ VC coming Fall</span>
      </section>
    </>
  );
}
